import ProductImage from 'coreClasses/product/Product/ProductImage';
import AbstractProductModel from 'coreClasses/product/AbstractProductModel';
import ManagerRepository from 'coreClasses/ManagerRepository';
import {Product as ProductType} from 'coreStore/data/products/types';
import {appendParamsToURL, parseURL} from 'coreUtils/url';

import {IProductImageOptions} from '../IProduct';

import {ICustomizationConfiguration} from './ICustomizedProduct';
import CustomizedProductPriceModel from './CustomizedProductPriceModel';

type CustomizedProductConstructor = new (
	managerRepository: ManagerRepository,
	product: ProductType,
	...args: any[]
) => AbstractProductModel;

/**
 * Represents CustomizedProductDecorator
 * @template T - Generic type extending the constructor.
 * @param {T} constructor - The constructor function to be decorated.
 * @returns {typeof constructor} - Decorated constructor function.
 */
function CustomizedProductDecorator<T extends CustomizedProductConstructor>(constructor: T) {
	const Base = constructor as new (...args: any[]) => AbstractProductModel;

	return class extends Base {
		private configuration: ICustomizationConfiguration;
		private images: ProductImage[];

		/**
		 * @constructor
		 * @param {ManagerRepository} managerRepository - manager repository
		 * @param {ProductType} product - product object
		 * @param {ICustomizationConfiguration} configuration - customization configuration
		 */
		constructor(
			managerRepository: ManagerRepository,
			product: ProductType,
			configuration: ICustomizationConfiguration,
		) {
			super(managerRepository, product);

			this.configuration = configuration;
		}

		/**
		 * Returns an array of images for the product for a selected view type.
		 * @param {String} viewType Image view type
		 * @param {iProductTransformationObject} transformationObject
		 * @returns {Array<ProductImage>} Array of images
		 */
		getProductImages(viewType: string, transformationObject: any = null): ProductImage[] {
			if (!this.images) {
				this.images = this.configuration.sides.map((side) => {
					return new ProductImage({
						disBaseLink: side.customImage,
						title: this.getName(),
						alt: this.getName(),
					}, transformationObject);
				});
			}

			return this.images;
		}

		/**
		 * Returns an image for the product for a selected view type and position.
		 * @param {iProductImageOptions} productImageOptions - the product image options
		 * @returns {ProductImage|null} - image object
		 */
		getProductImage({
			transformationObject = null,
			index = 0,
		}: IProductImageOptions) {
			return this.getProductImages(transformationObject as any)?.[index];
		}

		/**
		 * Returns the price model
		 * @returns {CustomizedProductPriceModel} - the price model
		 */
		getPriceModel() {
			return new CustomizedProductPriceModel(this.configuration.priceInfo);
		}

		/**
		 * Returns the raw slug
		 * @returns {string} slug url
		 */
		getSlugUrl(): string {
			let slugUrl: string = super.getSlugUrl();
			const queryParams: Record<string, string> = this.configuration?.url?.queryParams;

			if (slugUrl && queryParams) {
				slugUrl = appendParamsToURL(slugUrl, queryParams);
			}

			return slugUrl;
		}

		/**
		 * Returns product url
		 * @returns {string} product url
		 */
		getProductUrl() {
			const slugUrl = this.getSlugUrl();

			if (slugUrl) {
				const {pathname, search} = parseURL(slugUrl);
				let url = search ? `${pathname}${search}` : pathname;

				return this.managerRepository.getLocalesMgr().updateLinkWithLocale(url);
			}

			return '';
		}
	};
}

export default CustomizedProductDecorator;
